import type { Theme } from '@mui/material';
import type { AutocompleteClassKey } from '@mui/material/Autocomplete';
import type { Components } from '@mui/material/styles';

declare module '@mui/material/styles/overrides' {
  export interface ComponentNameToClassKey {
    MuiAutocomplete: AutocompleteClassKey;
  }
}

export const MuiTextField = (): Partial<Components> => ({
  MuiTextField: {
    styleOverrides: {
      root: {
        width: '100%',
        '& .Mui-disabled,& .Mui-disabled svg ': {
          color: 'rgba(0,0,0,0.3)',
        },
      },
    },
  },
});

export const MuiOutlinedInput = (baseTheme: Theme): Partial<Components> => ({
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        width: 'calc(100% / 0.875)',
        transform: 'scale(0.875)' /* simulate 14px */,
        transformOrigin: 'left center',
        // Desktop: 14px font size
        [baseTheme.breakpoints.up('md')]: {
          transform: 'none',
          width: '100%',
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(0, 0, 0, 0.26)',
        },
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&:not(.Mui-disabled):not(.Mui-error)': {
            '& .MuiOutlinedInput-notchedOutline': {
              border: `1px solid ${baseTheme.palette.brand.main}`,
            },
            '& .MuiAutocomplete-popupIndicator svg': {
              color: baseTheme.palette.brand.main,
            },
          },
        },

        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: baseTheme.palette.border,
        },
        '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${baseTheme.palette.brand.main}`,
        },
        '& .MuiSelect-icon': {
          right: '4px',
        },
        '&.Mui-focused .MuiSelect-icon': {
          color: baseTheme.palette.brand.main,
        },
        '&.Mui-error .MuiSelect-icon , &.Mui-error:not(.Mui-disabled) .MuiAutocomplete-endAdornment svg':
          {
            color: baseTheme.palette.error.main,
          },
        '&.Mui-error:hover .MuiOutlinedInput-notchedOutline,&.Mui-error': {
          borderWidth: '1px',
          borderColor: baseTheme.palette.error.main,
        },
        '&.Mui-disabled .MuiSelect-icon': {
          fill: 'rgba(0,0,0,0.3)',
        },
      },
      notchedOutline: {
        borderColor: baseTheme.palette.border,
      },
      input: {
        color: baseTheme.palette.text.primary,
        borderRadius: '8px',
        fontSize: '1rem',
        [baseTheme.breakpoints.up('md')]: {
          fontSize: '0.875rem', // 14px on desktop
        },
        padding: '8px 12px',
        '&.MuiSelect-select': {
          paddingRight: '40px !important',
        },
      },
    },
  },
});

export const MuiFilledInput = (baseTheme: Theme): Partial<Components> => ({
  MuiFilledInput: {
    styleOverrides: {
      root: {
        width: 'calc(100% / 0.875)',
        transform: 'scale(0.875)' /* simulate 14px */,
        transformOrigin: 'left center',
        // Desktop: 14px font size
        [baseTheme.breakpoints.up('md')]: {
          transform: 'none',
          width: '100%',
        },
        borderRadius: '8px',
        backgroundColor: baseTheme.palette.inputBackground,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: baseTheme.palette.border,
        paddingRight: '8px',
        '&:hover': {
          backgroundColor: 'transparent',
          borderColor: baseTheme.palette.primary.main,
        },
        '&:before, &:after': {
          display: 'none',
        },
        transition: baseTheme.transitions.create([
          'border-color',
          'background-color',
          'box-shadow',
        ]),
        '&.Mui-focused': {
          backgroundColor: baseTheme.palette.inputBackground,
          boxShadow: `${baseTheme.palette.inputBoxshadow} 0 0 5px 4px`,
          borderColor: baseTheme.palette.primary.main,
        },
        '&.Mui-error': {
          backgroundColor: baseTheme.palette.inputBackground,
        },
        '&.Mui-disabled': {
          backgroundColor: baseTheme.palette.inputBackground,
          'svg > path': {
            fill: baseTheme.palette.text.tertiary,
          },
          '&:hover': {
            borderColor: baseTheme.palette.text.tertiary,
          },
        },
        '& .MuiAutocomplete-clearIndicator svg': {
          width: '20px',
          height: '20px',
        },
        '& .MuiSelect-nativeInput ~ .MuiSvgIcon-root': {
          pointerEvents: 'none',
          position: 'absolute',
          right: '9px',
        },
      },
      input: {
        color: baseTheme.palette.text.primary,
        borderRadius: '8px',
        fontSize: '1rem',
        [baseTheme.breakpoints.up('md')]: {
          fontSize: '0.875rem', // 14px on desktop
        },
        '&:-webkit-autofill': {
          boxShadow: `0 0 0 100px inset ${baseTheme.palette.common.white}`,
        },
      },
    },
  },
});

export const MuiAutocomplete = (baseTheme: Theme): Partial<Components> => ({
  MuiAutocomplete: {
    styleOverrides: {
      popper: {
        '& > div': {
          borderRadius: '0',
          borderColor: baseTheme.palette.border,
        },
        '& .MuiAutocomplete-option': {
          minHeight: '32px',
          '&[aria-selected="true"]': {
            backgroundColor: baseTheme.bdColors.magnolia,
          },
          '&[data-focus="true"]': {
            backgroundColor: 'rgba(246,245,250, 0.6)',
          },
        },
      },

      inputRoot: {
        width: '100%',
        transform: 'none',
      },
    },
  },
});

export const MuiFormLabel = (baseTheme: Theme): Partial<Components> => ({
  MuiFormLabel: {
    styleOverrides: {
      root: {
        '&.Mui-focused:not(.Mui-error)': {
          color: baseTheme.palette.brand.main,
        },
        '&.Mui-error': {
          color: baseTheme.palette.error.main,
        },
        '&.Mui-disabled': {
          color: 'rgba(0,0,0,0.3)',
        },
        '&.MuiInputLabel-shrink': {
          color: baseTheme.palette.text.secondary,
          '&.Mui-error': {
            color: baseTheme.palette.error.main,
          },
        },
        '&.MuiInputLabel-root': {
          cursor: 'pointer',
          pointerEvents: 'none',
        },
      },
    },
  },
});

export const MuiCheckbox = (baseTheme: Theme): Partial<Components> => ({
  MuiCheckbox: {
    styleOverrides: {
      root: {
        '&.MuiCheckbox-colorPrimary.Mui-checked:hover': {
          backgroundColor: 'transparent',
        },
        '&.MuiCheckbox-colorPrimary svg': {
          fill: baseTheme.palette.label.dark,
          backgroundColor: 'transparent',
          fontSize: '23px',
        },
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
});
